import { AMEND_ORDER_SERVICE, IAmendOrderService } from '../amend-order';
import { APP_BRANDING_CONFIG_SERVICE, IAppBrandingConfigService } from '../app-branding-config';
import { DETAILS_VIEW_SERVICE, IDetailsViewService } from '../../services/details-view';
import { from } from 'rxjs';
import { GRID_ACTION_SERVICE, IGridActionService } from '../grid-action';
import {
    GridContextMenuItems,
    IGridContextMenuItems,
    IGridContextMenuParams,
    IGridContextMenuService,
} from './grid-context-menu.interface';
import { IDetailsViewInfo } from '../../interfaces';
import { inject, injectable } from 'inversify';
import { ITradingSettingsProviderService, TRADING_SETTINGS_PROVIDER_SERVICE } from '../trading-settings-provider';
import { map } from 'rxjs/operators';
import { pluckTyped } from '../../helpers';

@injectable()
export class GridContextMenuService implements IGridContextMenuService {
    @inject(GRID_ACTION_SERVICE) private readonly gridActionSrv: IGridActionService;
    @inject(AMEND_ORDER_SERVICE) private readonly amendOrderSrv: IAmendOrderService;
    @inject(TRADING_SETTINGS_PROVIDER_SERVICE) private readonly tradingSettingsSrv: ITradingSettingsProviderService;
    @inject(APP_BRANDING_CONFIG_SERVICE) private readonly appBrandingConfigSrv: IAppBrandingConfigService;
    @inject(DETAILS_VIEW_SERVICE) private readonly detailsViewSrv: IDetailsViewService;

    private isTradingDisabled = true;
    private isAmending = false;
    private isAmendPendingDisabled = false;
    private detailsViewInfo: IDetailsViewInfo = {
        orderList: [],
    };

    private tradingSettingsObs$ = null;
    private amendOrderObs$ = null;
    private appBrandingConfigObs$ = null;
    private detailsViewObs$ = null;

    private readonly TRADING_DISABLED_MESSAGE = 'Turn Trading ON to enable';
    private readonly IS_AMENDING_MESSAGE = 'You are already editing another order, please finish this action first';
    private readonly DETAILS_VIEW_DISABLED_MESSAGE = 'This order is already in the details view';

    private needInit() : boolean {
        return !Boolean(this.tradingSettingsObs$) || !Boolean(this.amendOrderObs$);
    }

    private init(): void {
        if (!this.tradingSettingsObs$) {
            this.tradingSettingsObs$ =  this.tradingSettingsSrv.stateObservable('tradingEnabled').pipe(
                map(({ value }) => value),
            ).subscribe(tradingEnabled => {
                this.isTradingDisabled = !tradingEnabled;
            });
        }

        if (!this.amendOrderObs$) {
            this.amendOrderObs$ = this.amendOrderSrv.asObservable().pipe(
                map(Boolean),
            ).subscribe((isAmending: boolean) => {
                this.isAmending = isAmending;
            });
        }

        if (!this.appBrandingConfigObs$) {
            this.appBrandingConfigObs$ = from(this.appBrandingConfigSrv.get()).pipe(
                pluckTyped('isAmendPendingDisabled'),
            ).subscribe((isAmendPendingDisabled: boolean) => {
                this.isAmendPendingDisabled = isAmendPendingDisabled;
            });
        }

        if (!this.detailsViewObs$) {
            this.detailsViewObs$ = this.detailsViewSrv.asObservable().subscribe((detailsViewInfo) => {
                this.detailsViewInfo = detailsViewInfo;
            });
        }
    }

    private readonly getCustomExportItem = (params: IGridContextMenuParams) : IGridContextMenuItems  => ({
        name: 'Export',
        icon: '<ion-icon src="/assets/icon/md/fnx_md-export.svg" class="ag-icon"/>',
        subMenu: [
            {
                name: 'CSV Export',
                icon: '<ion-icon src="/assets/icon/md/fnx_md-csv.svg" class="ag-icon"/>',
                action: () => this.gridActionSrv.next({ action: 'exportCsv', gridType: params.gridType }),
            },
            {
                name: 'Excel Export',
                icon: '<ion-icon src="/assets/icon/md/fnx_md-xls.svg" class="ag-icon"/>',
                action: () => this.gridActionSrv.next({ action: 'exportExcel', gridType: params.gridType }),
            },
            {
                name: 'PDF Export',
                icon: '<ion-icon src="/assets/icon/md/fnx_md-pdf.svg" class="ag-icon"/>',
                action: () => this.gridActionSrv.next({ action: 'exportPdf', gridType: params.gridType }),
            },
        ],
    });

    private readonly getAmendItem = (params: IGridContextMenuParams) : IGridContextMenuItems  => ({
        name: 'Amend',
        icon: '<ion-icon src="/assets/icon/md/fnx_md-create.svg" class="ag-icon"/>',
        disabled: this.isAmending || this.isTradingDisabled,
        tooltip: this.isAmending && this.IS_AMENDING_MESSAGE || this.isTradingDisabled && this.TRADING_DISABLED_MESSAGE,
        action: () => {
            this.gridActionSrv.next({ action: 'amend', dataItem: params.dataItem });
        },
    });

    private readonly getCancelItem = (params: IGridContextMenuParams) : IGridContextMenuItems  => ({
        name: 'Cancel',
        icon: '<ion-icon src="/assets/icon/md/fnx_md-bin.svg" class="ag-icon"/>',
        disabled: this.isTradingDisabled,
        tooltip: this.isTradingDisabled && this.TRADING_DISABLED_MESSAGE,
        action: () => {
            this.gridActionSrv.next({ action: 'cancel', dataItem: params.dataItem });
        },
    });

    private readonly getPendingBlotterItems = (params: IGridContextMenuParams) : GridContextMenuItems  => {
        if (params.gridType !== 'pending') {
            return [];
        }

        return [
            ...(this.isAmendPendingDisabled ? [] : [ this.getAmendItem(params) ]),
            this.getCancelItem(params),
            'separator',
        ];
    };

    private shouldDisableDetailsView(params: IGridContextMenuParams) : boolean {
        return params.gridType === this.detailsViewInfo.blotterType &&
            this.detailsViewInfo.orderList.includes(params.dataItem);
    }

    private readonly getViewDetailsItem = (params: IGridContextMenuParams) : IGridContextMenuItems  => {
        const isDetailsViewDisabled = this.shouldDisableDetailsView(params);

        return {
            name: 'View details',
            icon: '<ion-icon src="/assets/icon/md/fnx_md-view-details.svg" class="ag-icon"/>',
            disabled: isDetailsViewDisabled,
            tooltip: isDetailsViewDisabled ? this.DETAILS_VIEW_DISABLED_MESSAGE : '',
            action: () => {
                this.gridActionSrv.next({ action: 'viewDetails', dataItem: params.dataItem, gridType: params.gridType });
            },
        };
    };

    getMenuItems(params: IGridContextMenuParams): GridContextMenuItems {
        if (this.needInit()) {
            this.init();
        }

        return [
            ...this.getPendingBlotterItems(params),
            ...(params.gridType === 'kondor' ?
                params.defaultItems :
                [
                    ...params.defaultItems.filter(name => name !== 'export'),
                    this.getCustomExportItem(params),
                ]),
            this.getViewDetailsItem(params),
        ];
    }
}
